import { SepaScheme, TransactionType } from '@shared/transactions-types';
import type { DeepPartial } from '@luna/utils-ts';
import type { ExportFeatureConfig, TransactionsFeatureConfig } from '@shared/transactions-config';
import { isFeatureEnabled } from '@luna/gatekeeper';

const shouldUseExecutionDate = (): boolean =>
    isFeatureEnabled((f) => f.transactions.displayExecutionDate);

const shouldShowFavoriteField = (): boolean => isFeatureEnabled((f) => f.transactions.favorites);

export const transactions: DeepPartial<TransactionsFeatureConfig> = {
    showExecutionTimeForTransactionTypes: [TransactionType.INSTANT_PAYMENT],
    showExecutionTimeForSepaSchemes: [SepaScheme.INSTANT_PAYMENT],
    claims: {
        handover: {
            paramNames: [
                'transactionId',
                'transactionDate',
                'institute',
                'section',
                'language',
                'channel',
                'productCode',
                'conditionGroup',
                'client_id',
                'lang',
            ],
            paramValues: {
                channel: '09',
                productCode: 'CARDCLAIM',
                conditionGroup: 'CLAIM',
            },
        },
    },
};

export const exportFeatures: DeepPartial<ExportFeatureConfig> = {
    lastExportOption: true,
    fields: [
        { name: 'ownerAccountTitle', preselected: true, selectable: true },
        { name: 'ownerAccountNumber', preselected: true, selectable: true },
        {
            name: 'booking',
            preselected: () => !shouldUseExecutionDate(),
            selectable: true,
        },
        {
            name: 'execution',
            labelKey: 'export.fields.executionDate',
            condition: shouldUseExecutionDate,
            preselected: shouldUseExecutionDate,
            selectable: true,
        },
        { name: 'receiver', preselected: true, selectable: true },
        { name: 'amount', preselected: true, selectable: true },
        { name: 'currency', preselected: true, selectable: true },
        { name: 'reference', preselected: true, selectable: true },
        { name: 'referenceNumber', preselected: false, selectable: true },
        { name: 'note', preselected: false, selectable: true },
        {
            name: 'favorite',
            condition: shouldShowFavoriteField,
            preselected: false,
            selectable: true,
        },
        { name: 'valuation', preselected: false, selectable: true },
        { name: 'virtualCardNumber', preselected: false, selectable: true },
        { name: 'virtualCardDevice', preselected: false, selectable: true },
        {
            name: 'virtualCardMobilePaymentApplicationName',
            preselected: false,
            selectable: true,
        },
        { name: 'receiverReference', preselected: false, selectable: true },
        { name: 'sepaMandateId', preselected: false, selectable: true },
        { name: 'sepaCreditorId', preselected: false, selectable: true },
        { name: 'vopResult', preselected: true, selectable: true },
        { name: 'vopMatchedName', preselected: true, selectable: true },
    ],
    ofx: true,
    sortParams: () =>
        shouldUseExecutionDate()
            ? [
                  'EXECUTION_DATE_ASC',
                  'EXECUTION_DATE_DESC',
                  'BOOKING_DATE_ASC',
                  'BOOKING_DATE_DESC',
                  'AMOUNT_ASC',
                  'AMOUNT_DESC',
              ]
            : ['BOOKING_DATE_ASC', 'BOOKING_DATE_DESC', 'AMOUNT_ASC', 'AMOUNT_DESC'],
};
